<script setup>
import { computed, ref, watch } from 'vue';
import { CsTeleport } from '../teleport';
import { CsTransitionRoot } from '../transition';
import CsModalBackdrop from './CsModalBackdrop.vue';
import CsModalPanel from './CsModalPanel.vue';
import { ModalProvider } from './useModal';

/**
 * @typedef {object} ModalProps
 * @property {boolean} [isOpen=false] - Controls whether the modal is displayed
 * @property {boolean} [isShowCloseIcon=true] - Controls visibility of the close icon
 * @property {boolean} [isCloseOnBackdropClick=true] - Whether clicking the backdrop closes the modal
 * @property {boolean} [isCloseOnEscape=true] - Whether pressing Escape key closes the modal
 * @property {('sm'|'md'|'lg'|'xl'|'full')} [size='lg'] - Size of the modal
 * @property {('default'|'sm')} [verticalPadding='default'] - Vertical padding of modal panel
 * @property {number} [zIndex=1000] - zIndex of the modal
 * @property {boolean} [enableVerticalScroll=false] - Whether to enable vertical scrolling when content overflows
 */

/**
 * @type {ModalProps}
 */
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  isShowCloseIcon: {
    type: Boolean,
    default: true,
  },
  isCloseOnBackdropClick: {
    type: Boolean,
    default: true,
  },
  isCloseOnEscape: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String,
    default: 'lg', // sm, lg, full
  },
  verticalPadding: {
    type: String,
    default: 'default', // default, sm
  },
  zIndex: {
    type: Number,
    default: 1000,
  },
  enableVerticalScroll: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const hasModalHeader = ref(false);
const hasModalFooter = ref(false);

function onClose() {
  emit('close');
}

function onBackdropClick() {
  if (props.isCloseOnBackdropClick) {
    onClose();
  }
}

function onEscape(event) {
  if (event.key === 'Escape') {
    onClose();
  }
}

function registerEventListeners() {
  if (props.isCloseOnEscape) {
    window.addEventListener('keydown', onEscape);
  }
}

function unregisterEventListeners() {
  if (props.isCloseOnEscape) {
    window.removeEventListener('keydown', onEscape);
  }
}

function enableBodyScrollLock() {
  document.body.style.overflow = 'hidden';
}

function disableBodyScrollLock() {
  document.body.style.overflow = 'auto';
}

// Provide the modal context
ModalProvider({
  onClose,
  onBackdropClick,
  rootProps: computed(() => props),
  hasModalHeader,
  hasModalFooter,
});

// Register event listeners when the modal is opened
watch(
  () => props.isOpen,
  newValue => {
    if (newValue) {
      enableBodyScrollLock();
      registerEventListeners();
    } else {
      disableBodyScrollLock();
      unregisterEventListeners();
    }
  }
);
</script>

<template>
  <CsTeleport>
    <CsTransitionRoot
      :is-open="isOpen"
      data-component="CsModal"
      data-slot="root"
      role="dialog"
      :style="{
        '--modal-z-index': props.zIndex,
      }"
      class="relative z-[var(--modal-z-index)]"
    >
      <CsModalBackdrop />

      <CsModalPanel
        :size="props.size"
        :enable-vertical-scroll="props.enableVerticalScroll"
      >
        <slot />
      </CsModalPanel>
    </CsTransitionRoot>
  </CsTeleport>
</template>
